<template>
  <BaseModal
    :heading="$t('messages.details')"
    :parentRoute="{
      name: 'messages',
      query: filter ? { filter } : {}
    }"
  >
    <Flex direction="column">
      <Flex
        direction="column"
        align="center"
        :gap="0.5"
      >
        <BaseLabel
          v-if="message && message.status"
          :color="labelColor"
        >
          {{ $t(`messages.status.${message.status}`) }}
        </BaseLabel>
        <BaseText
          v-if="message && statusHelper"
          inline
        >
          {{ statusHelper }}
        </BaseText>
      </Flex>
      <BaseGrid
        v-if="message"
        container
      >
        <BaseGrid :size="12">
          <div
            :class="$style.message"
            class="ignore-theme"
            v-html="messageContent"
            v-test="'messageMessage'"
          />
          <BaseText
            v-if="
            message.gateway === 'email' &&
            message.gatewayMessage &&
            message.gatewayCode &&
            user.salonizedAdmin
          "
            size="s"
            v-test="'gatewayText'"
          >
            {{ message.gatewayMessage }}({{ message.gatewayCode }})
          </BaseText>
        </BaseGrid>
      </BaseGrid>
    </Flex>
    <template #footer>
      <div
        v-if="message && message.gateway"
        :class="$style.footer"
      >
        <BaseText
          v-if="message && message.customer && message.gateway"
          @click="goToCustomer(message.customer.id)"
        >
          {{ message.customer.fullName }}
        </BaseText>
        <BaseText
          v-if="
            message.gateway === 'email' &&
            message.customer &&
            message.customer.email
          "
          inline
        >
          - {{ message.customer.email }}
        </BaseText>
        <BaseText
          v-if="
            message.gateway === 'sms' &&
            message.customer &&
            message.customer.mobilePhone
          "
          inline
        >
          - {{ message.customer.mobilePhone }}
        </BaseText>
      </div>
    </template>
  </BaseModal>
</template>

<script lang="ts">
import gql from 'graphql-tag';
import { mapState } from 'pinia';
import { defineComponent } from 'vue';
import { useUserStore } from '@/stores/user';

export default defineComponent({
  computed: {
    ...mapState(useUserStore, ['user']),
    filter() {
      return this.$route.query?.filter;
    },
    statusHelper() {
      if (!this.message?.status) return '';
      const key = `messages.status.helpers.${this.message.status}`;
      const translation = this.$t(key);
      if (translation === key || !translation.trim()) return '';
      return translation;
    },
    labelColor() {
      if (!this.message || !this.message.status) {
        return;
      }

      if (
        this.message.status === 'sent' ||
        this.message.status === 'delivered'
      ) {
        return 'success';
      }

      if (
        this.message.status !== 'sent' &&
        this.message.status !== 'delivered' &&
        this.message.status !== 'pending'
      ) {
        return 'warning';
      }

      return 'default';
    },
    messageContent() {
      return this.message.renderedMessage || this.message.message || '';
    },
  },
  methods: {
    goToCustomer(customerId) {
      this.$router.push({ name: 'customer', params: { customerId } });
    },
  },
  apollo: {
    message: {
      query: gql`
        query getMessage($id: Int!) {
          message(id: $id) {
            id
            message
            renderedMessage
            messageType
            gateway
            gatewayCode
            gatewayMessage
            customer {
              id
              fullName
              mobilePhone
              email
            }
            emailTemplate {
              id
              hasLogo
              hasImage
              hasButton
              hasFooter
              subject
              message
              buttonUrl
              imageUrl
              discountMessage
              hasDiscount
              buttonTitle
              color
              discountCodeId
              discountCode {
                id
                code
              }
            }
            status
          }
        }
      `,
      skip() {
        return !this.$route.params.messageId;
      },
      variables() {
        return {
          id: Number.parseInt(this.$route.params.messageId),
        };
      },
      fetchPolicy: 'cache-and-network',
    },
  },
});
</script>

<style
  lang="scss"
  module
>
.message {
  overflow-x: auto;
  background-color: var(--color-bg-secondary);
  padding: $spacing;

  *,
  *:before,
  *:after {
    box-sizing: content-box;
  }

  p {
    margin-bottom: $spacing;
  }
}

.footer {
  display: flex;
  align-items: center;
  gap: $spacing * 0.5;
}
</style>
